import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaAward, FaTimes, FaMedal } from 'react-icons/fa';

const Credentials = ({ darkMode }) => {
    const { t } = useTranslation();
    const [selectedImage, setSelectedImage] = useState(null);

    const credentials = [
        {
            id: 1,
            title: t('credentials.degree.title'),
            institution: t('credentials.degree.institution'),
            honor: t('credentials.degree.honor'),
            date: t('credentials.degree.date'),
            image: `${process.env.PUBLIC_URL}/images/titulo-universitario.jpg`,
            thumbnail: `${process.env.PUBLIC_URL}/images/titulo-universitario.jpg`,
            type: 'degree'
        },
        {
            id: 2,
            title: t('credentials.certificate.title'),
            institution: t('credentials.certificate.institution'),
            honor: t('credentials.certificate.honor'),
            date: t('credentials.certificate.date'),
            image: `${process.env.PUBLIC_URL}/images/certificacion-universitaria.jpg`,
            thumbnail: `${process.env.PUBLIC_URL}/images/certificacion-universitaria.jpg`,
            type: 'certificate'
        }
    ];

    const openModal = (image) => {
        setSelectedImage(image);
    };

    const closeModal = () => {
        setSelectedImage(null);
    };

    return (
        <section id="credentials" className={`py-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 shadow-lg`}>
            <div className="text-center mb-8">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex items-center justify-center gap-3 mb-4"
                >
                    <FaAward className={`text-4xl ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                    <h2 className={`text-3xl font-bold ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                        {t('credentials.title')}
                    </h2>
                    <FaMedal className={`text-4xl ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                </motion.div>
                <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('credentials.subtitle')}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {credentials.map((credential, index) => (
                    <motion.div
                        key={credential.id}
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.2 }}
                        className={`relative overflow-hidden rounded-xl shadow-2xl ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        } hover:scale-105 transition-transform duration-300`}
                    >
                        <div className="relative group cursor-pointer" onClick={() => openModal(credential.image)}>
                            <img
                                src={credential.thumbnail}
                                alt={credential.title}
                                className="w-full h-80 object-cover"
                            />
                            <div className={`absolute inset-0 ${
                                darkMode ? 'bg-black/70' : 'bg-black/50'
                            } opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center`}>
                                <span className="text-white text-lg font-semibold">
                                    {t('credentials.clickToView')}
                                </span>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="flex items-center gap-2 mb-2">
                                <FaAward className={`text-2xl ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                                <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {credential.title}
                                </h3>
                            </div>
                            
                            <p className={`text-lg font-semibold mb-1 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                                {credential.institution}
                            </p>
                            
                            <div className={`inline-block px-3 py-1 rounded-full mb-2 ${
                                darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                                <span className="flex items-center gap-1 text-sm font-semibold">
                                    <FaMedal /> {credential.honor}
                                </span>
                            </div>
                            
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {credential.date}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </div>

            {selectedImage && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
                    onClick={closeModal}
                >
                    <button
                        className="absolute top-4 right-4 text-white hover:text-cyan-400 transition-colors"
                        onClick={closeModal}
                    >
                        <FaTimes size={32} />
                    </button>
                    <motion.img
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        src={selectedImage}
                        alt="Credential"
                        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </motion.div>
            )}
        </section>
    );
};

export default Credentials;
